import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import SearchableSelect from '../../components/SearchableSelect';
import { toUnidadMedidaSelectOptions } from '../../utils/unidadMedida';

export interface UmEquivalenciaLinea {
  id: string;
  umOrig: string;
  umDest: string;
  cantDest: number | '';
}

interface UnidadOption {
  idUnidadMedida: string;
  descripcion: string;
}

interface Props {
  umBase: string | null;
  lineas: UmEquivalenciaLinea[];
  unidadesMedida: UnidadOption[];
  onChange: (lineas: UmEquivalenciaLinea[]) => void;
  disabled?: boolean;
}

const newId = () => `eq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createEmptyEquivalencia = (umDestDefault?: string | null): UmEquivalenciaLinea => ({
  id: newId(),
  umOrig: '',
  umDest: umDestDefault?.trim() || '',
  cantDest: '',
});

const ProductoEquivalenciasTab: React.FC<Props> = ({
  umBase,
  lineas,
  unidadesMedida,
  onChange,
  disabled,
}) => {
  const options = toUnidadMedidaSelectOptions(unidadesMedida);

  const update = (id: string, patch: Partial<UmEquivalenciaLinea>) => {
    onChange(lineas.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        UM base del artículo: <strong>{umBase?.trim() || '(sin definir)'}</strong>. Convención: 1 UM origen = Cantidad UM destino.
        Ejemplo: 1 BOL = 25 KG → origen BOL, destino KG, cantidad 25.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>UM origen</TableCell>
              <TableCell>UM destino</TableCell>
              <TableCell width={160}>Cant. destino</TableCell>
              <TableCell width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.map((l) => (
              <TableRow key={l.id}>
                <TableCell>
                  <SearchableSelect
                    name={`umOrig-${l.id}`}
                    label=""
                    allowEmpty
                    emptyOptionLabel="UM origen"
                    value={l.umOrig || null}
                    onChange={(v) => update(l.id, { umOrig: v ?? '' })}
                    options={options}
                    margin="none"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <SearchableSelect
                    name={`umDest-${l.id}`}
                    label=""
                    allowEmpty
                    emptyOptionLabel="UM destino"
                    value={l.umDest || null}
                    onChange={(v) => update(l.id, { umDest: v ?? '' })}
                    options={options}
                    margin="none"
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    fullWidth
                    value={l.cantDest}
                    disabled={disabled}
                    onChange={(e) =>
                      update(l.id, {
                        cantDest: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    inputProps={{ min: 0, step: 'any' }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={() => onChange(lineas.filter((x) => x.id !== l.id))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {lineas.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    Sin equivalencias. Agregá al menos una si comprás en una UM distinta a la base.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={() => onChange([...lineas, createEmptyEquivalencia(umBase)])}
        >
          Agregar equivalencia
        </Button>
      </Box>
    </Stack>
  );
};

export default ProductoEquivalenciasTab;
